import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { ecommerceApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * Online carts — FR-ECOM-04
 * Checkout sessions, completions, abandonment recovery.
 */
export function CartsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [abandonedOnly, setAbandonedOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSweep, setShowSweep] = useState(false);

  const q = useQuery({
    queryKey: ['ecom-carts', statusFilter, abandonedOnly],
    queryFn: () => ecommerceApi.listCarts({
      status: statusFilter || undefined,
      abandoned: abandonedOnly || undefined,
      limit: 200,
    }),
  });

  const sweepMut = useMutation({
    mutationFn: () => ecommerceApi.markStaleCartsAbandoned(60),
    onSuccess: (d) => {
      toast.success(`${d.abandoned} carts marked abandoned`);
      qc.invalidateQueries({ queryKey: ['ecom-carts'] });
      setShowSweep(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = useMemo(() => {
    const rows = q.data ?? [];
    let active = 0, abandoned = 0, completed = 0, totalValue = 0;
    for (const c of rows) {
      if (c.status === 'active' || c.status === 'checking_out') active++;
      if (c.status === 'abandoned') abandoned++;
      if (c.status === 'completed') completed++;
      totalValue += parseFloat(c.total) || 0;
    }
    return { active, abandoned, completed, totalValue };
  }, [q.data]);

  const STATUS_OPTIONS = [
    { value: '',             label: 'All' },
    { value: 'active',       label: 'Active' },
    { value: 'checking_out', label: 'Checking out' },
    { value: 'completed',    label: 'Completed' },
    { value: 'abandoned',    label: 'Abandoned' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Online store"
        title="Carts"
        subtitle="Live checkout sessions. Identify drop-offs, recover abandoned carts."
        actions={
          <button
            className="btn-secondary"
            disabled={sweepMut.isPending}
            onClick={() => setShowSweep(true)}
          >
            <SweepIcon /> {sweepMut.isPending ? 'Sweeping…' : 'Sweep stale (>60m)'}
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Active / checking out" value={kpis.active}
          accent={kpis.active > 0 ? 'sage' : 'default'} />
        <KpiTile label="Completed" value={kpis.completed} accent="sage" />
        <KpiTile label="Abandoned" value={kpis.abandoned}
          accent={kpis.abandoned > 0 ? 'amber' : 'default'} />
        <KpiTile label="Total cart value" value={fmtMoney(kpis.totalValue)} />
      </div>

      {/* Filter bar */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center flex-wrap">
        <div className="flex gap-1">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={clsx(
                'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
                statusFilter === s.value
                  ? 'bg-canvas-subtle text-ink-900'
                  : 'text-ink-600 hover:bg-canvas-subtle'
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <label className="flex items-center gap-2 text-xs text-ink-700 pr-3 cursor-pointer">
          <input
            type="checkbox"
            checked={abandonedOnly}
            onChange={(e) => setAbandonedOnly(e.target.checked)}
            className="accent-sage-500"
          />
          <span>Unrecovered abandoned only</span>
        </label>
      </div>

      {q.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : q.error ? (
        <ErrorState error={q.error} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_28rem] gap-6">
          <div className="card overflow-hidden">
            {!q.data?.length ? (
              <EmptyState
                message="No carts match"
                hint="Try clearing filters or wait for online activity."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Storefront</th>
                      <th className="text-right">Items</th>
                      <th className="text-right">Total</th>
                      <th>Status</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(q.data ?? []).map((c: any) => {
                      const stale = c.abandoned_at && c.status !== 'completed' && c.status !== 'abandoned';
                      return (
                        <tr
                          key={c.id}
                          onClick={() => setSelectedId(c.id)}
                          className={clsx('cursor-pointer', selectedId === c.id && '!bg-canvas-subtle')}
                        >
                          <td>
                            <div className="text-sm font-medium text-ink-900 truncate max-w-[10rem]">
                              {c.customer_name ?? <span className="text-ink-500 italic">Guest</span>}
                            </div>
                            {c.email && (
                              <div className="text-xs text-ink-500 truncate max-w-[10rem]">{c.email}</div>
                            )}
                          </td>
                          <td className="font-mono text-xs text-ink-700">{c.storefront_code}</td>
                          <td className="text-right text-sm font-mono text-ink-700 tabular-nums">{c.item_count}</td>
                          <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                            ${parseFloat(c.total).toFixed(2)}
                          </td>
                          <td>
                            <div className="flex items-center gap-1.5">
                              <CartStatusBadge status={c.status} />
                              {stale && (
                                <span className="text-2xs font-semibold text-warning">stale</span>
                              )}
                            </div>
                          </td>
                          <td className="text-2xs text-ink-500 font-mono">
                            {new Date(c.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            {' '}
                            {new Date(c.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card p-5 min-h-[400px]">
            {!selectedId ? (
              <EmptyState
                message="Select a cart"
                hint="Click a row on the left to see items and recover or complete the checkout."
                icon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
                  </svg>
                }
              />
            ) : (
              <CartDetail
                cartId={selectedId}
                onChanged={() => qc.invalidateQueries({ queryKey: ['ecom-carts'] })}
              />
            )}
          </div>
        </div>
      )}

      {showSweep && (
        <Modal
          onClose={() => setShowSweep(false)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow mb-1">Commerce · Carts</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Sweep stale carts?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              All carts with no activity in the last 60 minutes will be marked as abandoned
              and a recovery email will be queued for each.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setShowSweep(false)}>Cancel</button>
            <button className="btn-primary" disabled={sweepMut.isPending} onClick={() => sweepMut.mutate()}>
              {sweepMut.isPending ? 'Sweeping…' : 'Sweep stale carts'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CartStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'active'       ? 'neutral' :
    status === 'checking_out' ? 'info' :
    status === 'completed'    ? 'sage' :
    status === 'abandoned'    ? 'warning' :
                                'neutral';
  return <StatusBadge status={status.replace('_', ' ')} tone={tone as any} />;
}

// ============================================================
// CartDetail
// ============================================================
function CartDetail({ cartId, onChanged }: { cartId: string; onChanged: () => void }) {
  const [showAbandon, setShowAbandon] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const q = useQuery({
    queryKey: ['ecom-cart', cartId],
    queryFn: () => ecommerceApi.getCart(cartId),
  });

  const completeMut = useMutation({
    mutationFn: () => ecommerceApi.completeCheckout(cartId),
    onSuccess: (d) => {
      toast.success(`Order ${d.orderNumber} created · ${fmtCurrency(d.grandTotal)}`, { duration: 6000 });
      q.refetch(); onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const abandonMut = useMutation({
    mutationFn: (reason: string) => ecommerceApi.abandonCart(cartId, { reason, sendRecoveryEmail: true }),
    onSuccess: () => { toast.success('Abandoned · recovery email queued'); q.refetch(); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} />;
  const cart = q.data;
  const cd = cart.cart_data ?? {};
  const items = cd.items ?? [];
  const totals = cd.totals ?? {};

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{cartId.slice(0, 8)}…</div>
          <div className="font-display font-bold text-lg text-ink-900 truncate">
            {cart.customer_name ?? <span className="text-ink-500 italic">Guest</span>}
          </div>
          {cart.email && <div className="text-xs text-ink-500 truncate">{cart.email}</div>}
          <div className="text-2xs text-ink-400 mt-1 font-mono">{cart.storefront_code}</div>
        </div>
        <CartStatusBadge status={cart.status} />
      </div>

      {cart.order_number && (
        <div className="bg-sage-50 border border-sage-200 rounded-xl px-3 py-2 text-xs text-sage-800 mb-3">
          Converted to order <span className="font-mono font-semibold">{cart.order_number}</span>
        </div>
      )}

      {/* Items */}
      <div className="eyebrow text-ink-500 mb-2">Items ({items.length})</div>
      <div className="border border-ink-100 rounded-xl overflow-hidden mb-4 max-h-64 overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-center text-ink-500 text-sm p-6">No items</div>
        ) : (
          <div className="divide-y divide-ink-100">
            {items.map((i: any, idx: number) => (
              <div key={idx} className="flex justify-between items-center px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink-900 truncate">{i.product_name}</div>
                  <div className="text-2xs text-ink-500 font-mono">
                    {i.sku} · {i.quantity} × ${i.unit_price}
                  </div>
                </div>
                <div className="font-mono font-semibold text-sm tabular-nums ml-3">
                  ${(i.quantity * i.unit_price).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Totals */}
      {totals && Object.keys(totals).length > 0 && (
        <div className="bg-canvas-subtle rounded-xl p-4 mb-4">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-ink-600">
              <span>Subtotal</span>
              <span className="font-mono tabular-nums">{fmtCurrency((totals.subtotal ?? 0))}</span>
            </div>
            {totals.tax_total > 0 && (
              <div className="flex justify-between text-ink-600">
                <span>Tax</span>
                <span className="font-mono tabular-nums">{fmtCurrency(totals.tax_total)}</span>
              </div>
            )}
            {totals.shipping_total > 0 && (
              <div className="flex justify-between text-ink-600">
                <span>Shipping</span>
                <span className="font-mono tabular-nums">{fmtCurrency(totals.shipping_total)}</span>
              </div>
            )}
          </div>
          <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-ink-200">
            <span className="font-semibold text-ink-900">Total</span>
            <span className="font-display font-bold text-2xl text-ink-900 tabular-nums tracking-tight">
              ${(totals.grand_total ?? 0).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Shipping address */}
      {cd.shipping_address && (
        <div className="mb-4">
          <div className="eyebrow text-ink-500 mb-2">Shipping to</div>
          <div className="bg-canvas-subtle rounded-xl px-3 py-2.5 text-sm text-ink-700 leading-relaxed">
            <div className="font-semibold">{cd.shipping_address.name}</div>
            <div>{cd.shipping_address.line1}</div>
            <div>
              {cd.shipping_address.city}
              {cd.shipping_address.state && `, ${cd.shipping_address.state}`}
              {cd.shipping_address.postalCode && ` ${cd.shipping_address.postalCode}`}
            </div>
          </div>
        </div>
      )}

      {/* Abandonment info */}
      {cart.abandoned_at && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900 mb-4">
          <div className="font-semibold">Abandoned</div>
          <div className="mt-0.5">
            {new Date(cart.abandoned_at).toLocaleString()}
            {cart.abandoned_reason && <> · {cart.abandoned_reason}</>}
          </div>
          {cart.recovery_email_sent_at && (
            <div className="mt-1 text-amber-700">
              Recovery email sent {new Date(cart.recovery_email_sent_at).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 justify-end flex-wrap">
        {['active', 'checking_out'].includes(cart.status) && (
          <button
            className="text-xs font-medium text-ink-500 hover:text-danger transition-colors px-3 py-1.5"
            onClick={() => setShowAbandon(true)}
          >
            Abandon
          </button>
        )}
        {cart.status === 'checking_out' && (
          <button
            className="btn-primary"
            disabled={completeMut.isPending}
            onClick={() => setShowComplete(true)}
          >
            {completeMut.isPending ? 'Completing…' : 'Complete → Create order'}
          </button>
        )}
      </div>

      {showAbandon && (
        <AbandonCartDialog
          loading={abandonMut.isPending}
          onClose={() => setShowAbandon(false)}
          onConfirm={(reason) => abandonMut.mutate(reason)}
        />
      )}
      {showComplete && (
        <Modal
          onClose={() => setShowComplete(false)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow mb-1">Commerce · Checkout</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Complete checkout?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              A confirmed sales order will be created from this cart. The cart will be locked.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setShowComplete(false)}>Cancel</button>
            <button className="btn-primary" disabled={completeMut.isPending} onClick={() => completeMut.mutate()}>
              {completeMut.isPending ? 'Completing…' : 'Complete checkout'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AbandonCartDialog({ loading, onClose, onConfirm }: {
  loading: boolean; onClose: () => void; onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · Cart</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Abandon this cart?</h2>
        <p className="text-sm text-ink-500 mt-1 leading-relaxed">
          A recovery email will be queued for the customer.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. customer requested cancellation" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700" disabled={loading}
          onClick={() => onConfirm(reason)}>
          {loading ? 'Abandoning…' : 'Abandon cart'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function SweepIcon() { return <svg {...iconProps}><path d="M3 21l6-6"/><path d="M13 5l6 6"/><path d="M9 15l6-6"/></svg>; }
